/**
 * A live alarm's raw identity, made readable — shared by the toasts, the bell and the activity
 * drawer so the three never name one alarm three ways.
 *
 * `LiveAlarm.name` is the trigger's raw identity (`alarm_notify()`, migration 0026):
 * `sungrow_phase_b_voltage:above:10m`, `sungrow_fault_code=552`. A person should never read
 * that. The full resolver is `modules/overview/alarm-label.ts`, but it imports `device-facts.ts`
 * and everything here is reached from the NOT-lazy shell — measured: that import moved the
 * gzipped entry from 189.3 KB to 208.8 KB, over the 200 KB budget (2026-09-28). So this is the
 * few-line version: it reads the shape of the identity, not the catalogue — plus SunGrow's own
 * code table (`lib/alarms/sungrow-titles.ts`, no other imports), so a toast reads "PV abnormal
 * alarm — string 5" and "Grid voltage high on phase C", never `sungrow_fault_code=552`. A live
 * frame carries the identity, not the catalogue's label, so a toast names a code by SunGrow's
 * default — the same name the board shows until somebody renames the code on Fault codes.
 */
import type { LiveAlarm } from '@/lib/api/events';
import { sungrowCodeWords, sungrowStateWords } from '@/lib/alarms/sungrow-titles';

function words(k: string): string {
  // "phase_b_voltage" → "phase B voltage": a lone letter is a phase or a string, and reads as one.
  const w = k
    .split('_')
    .filter(Boolean)
    .map((p) => (p.length === 1 ? p.toUpperCase() : p))
    .join(' ');
  return w.charAt(0).toUpperCase() + w.slice(1);
}

export function readableAlarmName(name: string): string {
  const t = /^([a-z0-9_]+):(below|above)(?::(\d+)m)?$/.exec(name);
  const phase = t ? /^sungrow_phase_([abc])_voltage$/.exec(t[1]!) : null;
  if (t && phase) return `Grid voltage ${t[2] === 'below' ? 'low' : 'high'} on phase ${phase[1]!.toUpperCase()}`;
  if (t && (t[1] === 'sungrow_grid_frequency' || t[1] === 'freq')) return `Grid frequency ${t[2] === 'below' ? 'low' : 'high'}`;
  if (t) {
    const held = t[3] ? ` for ${t[3]} min` : '';
    return `${words(t[1]!)} ${t[2] === 'below' ? 'low' : 'high'}${held}`;
  }
  const r = /^([a-z0-9_]+)=(-?\d+(?:\.\d+)?)$/.exec(name);
  if (r && r[1] === 'sungrow_fault_code') return sungrowCodeWords(Number(r[2]))?.title ?? `Inverter reported code ${r[2]}`;
  if (r && r[1] === 'sungrow_work_state') {
    const st = sungrowStateWords(Number(r[2]));
    if (st) return st.title;
  }
  if (r) return `${words(r[1]!)} ${r[2]}`;
  if (/^[a-z0-9]+(?:_[a-z0-9]+)+$/.test(name)) return words(name);
  return name;
}

/** A rule alarm's identity: `rule:<uuid>` (`modules/rules/engine.ts`). */
const RULE_IDENTITY = /^rule:([0-9a-f-]{36})$/i;

export function isRuleIdentity(name: string): boolean {
  return RULE_IDENTITY.test(name);
}

/**
 * `ruleName` names a rule alarm by the rule's own name — never the uuid. Until the rule list
 * has loaded (or for a rule since deleted) it reads "Rule alarm", which is true and not a uuid.
 */
export function liveAlarmLabel(
  a: Pick<LiveAlarm, 'name' | 'device'>,
  ruleName?: (id: string) => string | undefined,
): string {
  if (a.name === 'comm_loss') return a.device === 'All devices' ? 'No data received (platform)' : 'Communication lost';
  const rule = RULE_IDENTITY.exec(a.name);
  if (rule) return ruleName?.(rule[1]!) ?? 'Rule alarm';
  return readableAlarmName(a.name);
}
